import { FhirService } from './../core/service/fhir.service';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { SessionSetting } from '../core/model/session-setting';
import { ChannelService } from '../core/service/channel.service';
import { CommunityProviderProgramService } from '../core/service/community-provider-program.service';
import { MessageSubject, MessengerService } from '../core/service/messenger.service';
import { SettingService } from '../core/service/setting.service';
import { MessageHandlerService } from '../core/utility/message-handler.service';
import { CaseService } from '../core/service/case.service';
import { SessionStorageService } from 'ngx-webstorage';
import { IntlService } from '@progress/kendo-angular-intl';

export interface ReferralEvent
{
  referralReassign: boolean;
  parentCaseId: number;
}

@Component({
  standalone: false,
  selector: 'app-referral',
  templateUrl: './referral.component.html',
  styleUrls: ['./referral.component.scss']
})
export class ReferralComponent implements OnDestroy, OnInit
{
  // Event used when the Task Detail have a Complete Assessment and returns services categories,
  // we need to process referrals before completing the task
  @Output() public afterReferralProcessing: EventEmitter<ReferralEvent> = new EventEmitter();

  @Input() public memberId: number;
  @Input() public caseId: number;     // Use when Referral is initiated from the case
  @Input() public context: string;    // Use to control which component is responsible to handle searchForServicesToReferred event

  // New Referral Type selection dialog open
  public newReferralTypeDialogOpen = false;

  // New Referral search dialog open
  public newReferralDialogOpen = false;

  // Service category ids
  public serviceCategories = [];

  // Services excluded ids
  public excludedServices = [];
  public referralReassign = false;

  // When assessment is trigerred from a workflow assessment, we keep track of the parentCaseId to make sure the Parent channel id is set.
  public parentCaseId = 0;

  public newReferralCompleted: string;
  public referralProcessed: string;
  public existingReferral: string;

  // Event subscription for automatic community search after add/update assessment.
  private messagingSubscription: Subscription;

  constructor(
    private caseService: CaseService,
    private messengerService: MessengerService,
    private settingService: SettingService,
    private translate: TranslateService,
    private toastrService: ToastrService,
    private router: Router,
    private fhirService: FhirService,
    private communityProviderProgramService: CommunityProviderProgramService,
    private messageHandlerService: MessageHandlerService,
    private channelService: ChannelService,
    private storageService: SessionStorageService,
    public intl: IntlService)
  {
    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'searchForServicesToReferred' && message.data.context === this.context)
        {
          this.serviceCategories = message.data.categories;
          this.excludedServices = message.data.excludedServices;
          this.referralReassign = message.data.referralReassign;
          this.parentCaseId = message.data.parentCaseId !== undefined &&
            message.data.parentCaseId !== null && message.data.parentCaseId > 0 ? message.data.parentCaseId : 0;
          this.newReferralDialogOpen = true;
          this.storageService.store('newReferralDialogOpen', this.newReferralDialogOpen);
        }
      }
    });
  }

  public ngOnInit(): void
  {
    this.translate.get(['REFERRAL-PROCESSED-SUCCESSFULLY', 'EXISTING-REFERRAL', 'NEW-REFERRAL-COMPLETED']).subscribe({
      next: (res) =>
      {
        this.referralProcessed = res['REFERRAL-PROCESSED-SUCCESSFULLY'];
        this.existingReferral = res['EXISTING-REFERRAL'];
        this.newReferralCompleted = res['NEW-REFERRAL-COMPLETED'];
      }
    });
    this.storageService.store('newReferralDialogOpen', false);
  }

  public ngOnDestroy(): void
  {
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
  }

  /**
   * Provide a new referral and open the referral type selection dialog.
   */
  public newReferral(enabledAssessment = true): void
  {
    this.referralReassign = false;
    this.excludedServices = [];
    this.serviceCategories = [];
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        // Show user warning if patient identifiers are not set. No default is provided.
        if (sessionSetting.defaultChannelForReferral.trim() === '')
        {
          this.translate.get('WARNING-CHANNEL-DEFAULT-NOT-DEFINED').subscribe({
            next: (res) =>
            {
              this.toastrService.warning(res, null,
                {
                  enableHtml: true,
                  timeOut: 10000     // Use a longer timeout than usual
                })
                .onTap.subscribe({
                  next: () =>
                  {
                    if (!this.fhirService.embedded || this.fhirService.fullSite)
                    {
                      this.router.navigate(['/system-settings']);
                    }
                  }
                });
            }
          });
        }
        else
        {
          this.channelService.getMinimalChannelById(Number.parseFloat(sessionSetting.defaultChannelForReferral)).subscribe({
            next: (channels) =>
            {
              if (channels.length === 1)
              {
                channels.forEach(d =>
                {
                  if (d.status !== 'Online')
                  {
                    this.translate.get('WARNING-CHANNEL-DEFAULT-NOT-ONLINE').subscribe({
                      next: (res) =>
                      {
                        this.toastrService.warning(res, null,
                          {
                            enableHtml: true,
                            timeOut: 10000     // Use a longer timeout than usual
                          })
                          .onTap.subscribe({
                            next: () =>
                            {
                              if (!this.fhirService.embedded || this.fhirService.fullSite)
                              {
                                this.router.navigate(['/channel-edit/' + sessionSetting.defaultChannelForReferral]);
                              }
                            }
                          });
                      }
                    });
                  }
                  else
                  {
                    document.body.style.overflow = 'hidden';
                    this.newReferralTypeDialogOpen = enabledAssessment;
                    // Check if assessments is allowed from EHR. If not, skip selection dialog and open services search automatically.
                    if (this.fhirService.embedded && !enabledAssessment)
                    {
                      this.onSelectNewReferralType('SEARCH-FOR-SERVICES');
                    }
                  }
                });
              }
              else
              {
                this.messageHandlerService.displayWarning('CHANNEL-NOT-FOUND');
              }
            }
          });
        }
      }
    });
  }

  /**
   * Event when a new referral type have been selected.
   *
   * @param type Type of the referral.
   */
  public onSelectNewReferralType(type: string): void
  {
    this.newReferralTypeDialogOpen = false;
    switch (type)
    {
      case 'ASSESSMENTS':
        document.body.style.overflow = '';
        this.messengerService.sendMessage(new MessageSubject('openAddAssessmentDialog'));
        break;
      case 'SEARCH-FOR-SERVICES':
        this.newReferralDialogOpen = true;
        this.storageService.store('newReferralDialogOpen', this.newReferralDialogOpen);
        break;
    }
  }

  /**
   * New Referral selected have been cancelled.
   *
   * @param isCloseOrCancelReferral If Referral window is either Close or Cancel, true will be returned.
   *                                After completing the referral, false will be returned and call afterReferralProcessing.emit().
   */
  public closeNewReferral(isCloseOrCancelReferral: boolean): void
  {
    document.body.style.overflow = '';
    this.newReferralDialogOpen = false;
    if (isCloseOrCancelReferral)
    {
      this.storageService.store('newReferralDialogOpen', this.newReferralDialogOpen);
      this.afterReferralProcessing.emit();
    }
  }

  /**
   * Send selected referrals.
   *
   * @param referralIds List of referral programs ids.
   */
  public onSendReferrals(referralInfo: { selectedItems: number[]; sendEmailNotification: boolean }): void
  {
    const objectToPersist: any = {};
    objectToPersist.memberId = this.memberId;
    objectToPersist.selectedServices = referralInfo.selectedItems;
    objectToPersist.sendEmailNotification = referralInfo.sendEmailNotification;
    objectToPersist.parentCaseId = this.parentCaseId ? this.parentCaseId : (this.caseId ? this.caseId : null);

    this.communityProviderProgramService.sendReferrals(objectToPersist).subscribe({
      next: (data) =>
      {
        let messageText = '';
        if (data.newCases.length > 0)
        {
          this.toastrService.success('<br/>' + this.referralProcessed + '<br/>', this.newReferralCompleted,
            {
              enableHtml: true,
              disableTimeOut: false,
              tapToDismiss: false,
              toastClass: 'ngx-toastr toast-success channel-message'
            });
        }
        if (data.existingCases.length > 0)
        {
          data.existingCases.forEach((existingCase) =>
          {
            const referredDate = existingCase.referredDate !== null ? this.intl.formatDate(this.intl.parseDate(existingCase.referredDate), 'd') : '';
            messageText += '<br/>' + this.existingReferral.replace('{{referredTo}}', existingCase.referredTo).replace('{{referredDate}}', referredDate) + '<br/>'
          });
          this.toastrService.warning(messageText, null,
            {
              enableHtml: true,
              disableTimeOut: true,
              tapToDismiss: false,
              toastClass: 'ngx-toastr toast-warning channel-message'
            });
        }

        if (!this.referralReassign)
        {
          this.messengerService.sendMessage(new MessageSubject('reloadReferrals'));
          this.messengerService.sendMessage(new MessageSubject('reloadPinnedResources'));
        }
        // We can emit() event because referral is completed.
        setTimeout(() =>
        {
          this.afterReferralProcessing.emit({
            referralReassign: this.referralReassign,
            parentCaseId: this.parentCaseId
          });
        });
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Common method shared between Patient and Case when referral reassign button is pressed
   * 
   * @param e ReferralEvent
   */
  public completeReferralReassigment(e: ReferralEvent): void 
  {
    if (e && e.referralReassign && e.parentCaseId > 0)
    {
      this.translate.get(['CASE-CLOSED-SUCCESSFULLY', 'REFERRAL-REASSIGNED']).subscribe({
        next: (res) =>
        {
          this.caseService.closeCase(e.parentCaseId, res['REFERRAL-REASSIGNED'], null)
            .subscribe({
              next: () =>
              {
                this.messageHandlerService.displayDirectMessage('success', res['CASE-CLOSED-SUCCESSFULLY']
                  .replace('{{caseId}}', e.parentCaseId));
                this.messengerService.sendMessage(new MessageSubject('reloadReferrals'));
              },
              error: (errorResponse) =>
              {
                this.messageHandlerService.displayError(errorResponse);
              }
            });
        }
      });
    }
  }
}
